import { REQUEST_CONFIGS_KEYS } from '@services/constants'

import { STEP1FIELDS, useStep1State } from './initialStates/step1State'
import { STEP2FIELDS, useStep2State } from './initialStates/step2State'
import { STEP3FIELDS, useStep3State } from './initialStates/step3State'
import { STEP4_1FIELDS, useStep4_1State } from './initialStates/step4_1State'
import { STEP5FIELDS, useStep5State } from './initialStates/step5State'
import { STEP6FIELDS, useStep6State } from './initialStates/step6State'

import {
	isGR7_1RenovationOrDevelopment,
	isGR7_2RequestOccupation,
	isRenovationSectionA,
	isRenovationSectionB,
	isCase1SectionA,
	isCase4SectionA,
	isCase3or5or6SectionA,
	isCase3SectionA,
	isCase5SectionA,
	isCase6SectionA,
	isOccupationSectionA,
	isOccupationSectionB,
	isOccupationSectionC,
	isOccupationSectionD,
	isOccupationSectionE,
	isGR7_3HomeOfficeOrBusiness,
	isGR7_4InstallationOfEnseigne,
	isArtisticWallRepresentationInstallation,
	isNewSignInstallation,
	isReplaceSignInstallation
} from './__index.utils'

import * as pageUtils from '../__forms.utils'
import { STEP4_2FIELDS, useStep4_2State } from './initialStates/step4_2State'
import { STEP4_3FIELDS, useStep4_3State } from './initialStates/step4_3State'
import { STEP4_4FIELDS, useStep4_4State } from './initialStates/step4_4State'

// --- 1. Initial state HOOK ---
export function useInitialFormState() {
	return {
		...useStep1State(),
		...useStep2State(),
		...useStep3State(),
		...useStep4_1State(),
		...useStep4_2State(),
		...useStep4_3State(),
		...useStep4_4State(),
		...useStep5State(),
		...useStep6State()
	}
}

// --- 2. Logique modulaire des dépendances dynamiques ---
function updateLinkedFields(state, changedField: string, value: string) {
	type StateType = ReturnType<typeof useInitialFormState>
	const updates: Partial<StateType> = {}

	switch (changedField) {
		// ! STEP 1
		/**  @whatToDo - Que souhaitez-vous faire? */
		case STEP1FIELDS.whatToDo: {
			const isGR7_1 = isGR7_1RenovationOrDevelopment(value)
			const isGR7_2 = isGR7_2RequestOccupation(value)
			const isGR7_3 = isGR7_3HomeOfficeOrBusiness(value)
			const isGR7_4 = isGR7_4InstallationOfEnseigne(value)

			// ************************************************
			// PIIA reset fields
			updates.isSubjectToPiiaApproval = {
				...state.isSubjectToPiiaApproval,
				required: isGR7_1 || isGR7_4,
				value: ''
			}

			const piiaFieldsToReset = [
				STEP1FIELDS.hasBeenSubjectToApproval,
				STEP1FIELDS.iaNumber,
				STEP1FIELDS.ecResolutionNumber
			]

			piiaFieldsToReset.forEach((key) => {
				if (state[key]) {
					updates[key] = {
						...state[key],
						required: false,
						value: ''
					}
				}
			})

			//************************************************
			// reset fields of Step 2 based on the selected option in Step 1
			updates.isLocationOwned = {
				...state.isLocationOwned,
				value: ''
			}

			const step2FieldsToReset = [
				STEP2FIELDS.ownerFirstName,
				STEP2FIELDS.ownerLastName,
				STEP2FIELDS.ownerEmail,
				STEP2FIELDS.ownerPhoneNumber
			]

			step2FieldsToReset.forEach((key) => {
				if (state[key]) {
					updates[key] = {
						...state[key],
						required: false,
						value: ''
					}
				}
			})

			// ************************************************
			// ** Update Step 3 fields
			updates.mainUseOfExistingBuilding = {
				...state.mainUseOfExistingBuilding,
				required: isGR7_1 || isGR7_3 || isGR7_4,
				value: '' // reset value
			}

			updates.doYouWantToChangeCurrentUse = {
				...state.doYouWantToChangeCurrentUse,
				required: isGR7_1,
				value: !isGR7_1 ? '' : state.doYouWantToChangeCurrentUse.value
			}

			updates.projectedUse = {
				...state.projectedUse,
				required: isGR7_1 && state.doYouWantToChangeCurrentUse.value === 'true',
				value: isGR7_1 ? state.projectedUse.value : ''
			}

			updates.existingMainBuildingIsShared = {
				...state.existingMainBuildingIsShared,
				required: isGR7_1 || isGR7_3 || isGR7_4,
				value: ''
			}

			// ************************************************

			// ************************************************
			// !important RESET FIELDS of Steps 4.1 and 4.2
			const step4_1FieldsToReset = Object.values(STEP4_1FIELDS)
			const step4_2FieldsToReset = Object.values(STEP4_2FIELDS)
			const step4_3FieldsToReset = Object.values(STEP4_3FIELDS)
			const step4_4FieldsToReset = Object.values(STEP4_4FIELDS)

			const fieldsToReset = [
				...step4_1FieldsToReset,
				...step4_2FieldsToReset,
				...step4_3FieldsToReset,
				...step4_4FieldsToReset
			]

			fieldsToReset.forEach((value) => {
				if (state[value]) {
					switch (value) {
						case STEP4_1FIELDS.typesOfWorkRenovation:
							updates.typesOfWorkRenovation = {
								...state.typesOfWorkRenovation,
								required: isGR7_1,
								value: ''
							}
							break
						case STEP4_2FIELDS.typesOfWorkOccupation:
							updates.typesOfWorkOccupation = {
								...state.typesOfWorkOccupation,
								required: isGR7_2,
								value: ''
							}
							break
						// --- 4.3 Home Office or Business
						case STEP4_3FIELDS.typesOfWorkHomeOffice:
							updates.typesOfWorkHomeOffice = {
								...state.typesOfWorkHomeOffice,
								required: isGR7_3,
								value: ''
							}
							break
						case STEP4_3FIELDS.occupiedAreaHomeOffice:
							updates.occupiedAreaHomeOffice = {
								...state.occupiedAreaHomeOffice,
								required: isGR7_3,
								value: ''
							}
							break
						case STEP4_3FIELDS.occupationActivityDescriptionHomeOffice:
							updates.occupationActivityDescriptionHomeOffice = {
								...state.occupationActivityDescriptionHomeOffice,
								required: isGR7_3,
								value: ''
							}
							break
						case STEP4_3FIELDS.isSignInstallationPlanned:
							updates.isSignInstallationPlanned = {
								...state.isSignInstallationPlanned,
								required: isGR7_3,
								value: ''
							}
							break
						case STEP4_3FIELDS.startDateHomeOffice:
							updates.startDateHomeOffice = {
								...state.startDateHomeOffice,
								required: isGR7_3,
								value: ''
							}
							break
						// --- 4.4 Enseigne
						case STEP4_4FIELDS.typesOfWorkEnseigne:
							updates.typesOfWorkEnseigne = {
								...state.typesOfWorkEnseigne,
								required: isGR7_4,
								value: ''
							}
							break
						case STEP4_4FIELDS.totalSignArea:
							updates.totalSignArea = {
								...state.totalSignArea,
								required: isGR7_4,
								value: ''
							}
							break
						case STEP4_4FIELDS.approximateCostOfWorkEnseigne:
							updates.approximateCostOfWorkEnseigne = {
								...state.approximateCostOfWorkEnseigne,
								required: isGR7_4,
								value: ''
							}
							break
							

						default:
							updates[value] = {
								...state[value],
								required: false,
								value: ''
							}
					}
				}
			})

			// ************************************************
			// ** Reset Step 6 fields
			// ************************************************
			updates.isWaterAndSewerConnectionRequired = {
				...state.isWaterAndSewerConnectionRequired,
				required: isGR7_1,
				value: isGR7_1 ? state.isWaterAndSewerConnectionRequired.value : ''
			}
			break
		}

		/**  @requestAs - Q2 Vous faites cette demande en tant que: */
		case STEP1FIELDS.requestAs: {
			const isEntreprise = value == REQUEST_CONFIGS_KEYS.requestAs.entreprise

			updates.companyName = {
				...state.companyName,
				required: isEntreprise,
				value: isEntreprise ? state.companyName.value : ''
			}
			updates.representativeName = {
				...state.representativeName,
				required: isEntreprise,
				value: isEntreprise ? state.representativeName.value : ''
			}

			// reset whoWillBeInChargeOfWork
			if (
				value == REQUEST_CONFIGS_KEYS.requestAs.individual &&
				state.whoWillBeInChargeOfWork.value ==
					REQUEST_CONFIGS_KEYS.whoWillBeInChargeOfWork.firstOption
			) {
				updates.whoWillBeInChargeOfWork = {
					...state.whoWillBeInChargeOfWork,
					value: ''
				}
			}
			break
		}

		/** @isSubjectToPiiaApproval - Est-ce que les travaux sont assujettis à une approbation PIIA? */
		case STEP1FIELDS.isSubjectToPiiaApproval: {
			const isYesPiiaApproval = value == 'true'
			updates.hasBeenSubjectToApproval = {
				...state.hasBeenSubjectToApproval,
				required: isYesPiiaApproval,
				value: isYesPiiaApproval ? state.hasBeenSubjectToApproval.value : ''
			}
			updates.iaNumber = {
				...state.iaNumber,
				required:
					isYesPiiaApproval && state.hasBeenSubjectToApproval.value === 'true',
				value: isYesPiiaApproval ? state.iaNumber.value : ''
			}
			break
		}

		/**  @hasBeenSubjectToApproval - Le projet a-t-il reçu une approbation PIIA? */
		case STEP1FIELDS.hasBeenSubjectToApproval: {
			const isYesHasBeenSubject = value == 'true'
			updates.iaNumber = {
				...state.iaNumber,
				required:
					isYesHasBeenSubject && state.isSubjectToPiiaApproval.value === 'true',
				value: isYesHasBeenSubject ? state.iaNumber.value : ''
			}
			break
		}

		// ***********************************************************
		// ! STEP 2
		case STEP2FIELDS.isSameLocation: {
			updates.localNumber = {
				...state.localNumber,
				value: value == 'true' ? state.localNumber.value : ''
			}
			break
		}
		/**  @isLocationOwned */
		case STEP2FIELDS.isLocationOwned: {
			const isYes = value == 'false' && !isGR7_2RequestOccupation(state.whatToDo.value) 

			// updates.ownerFirstName = {
			// 	...state.ownerFirstName,
			// 	required: isYes
			// }
			// updates.ownerLastName = {
			// 	...state.ownerLastName,
			// 	required: isYes
			// }
			// updates.ownerEmail = {
			// 	...state.ownerEmail,
			// 	required: isYes
			// }
			// updates.ownerPhoneNumber = {
			// 	...state.ownerPhoneNumber,
			// 	required: isYes
			// }

			const fieldsToMakeUpdate = [
				STEP2FIELDS.ownerFirstName,
				STEP2FIELDS.ownerLastName,
				STEP2FIELDS.ownerEmail,
				STEP2FIELDS.ownerPhoneNumber
			]

			fieldsToMakeUpdate.forEach((key) => {
				if (state[key]) {
					updates[key] = {
						...state[key],
						required: isYes,
						value: isYes ? state[key].value : ''
					}
				}
			})

			break
		}

		// ***********************************************************
		// ! STEP 3.1
		case STEP3FIELDS.mainUseOfExistingBuilding: {
			updates.projectedUse = {
				...state.projectedUse,
				value: ''
			}
			break
		}

		// Souhaitez-vous effectuer un changement à l'usage actuel du bâtiment?
		case STEP3FIELDS.doYouWantToChangeCurrentUse: {
			const isRequired = value == 'true'
			updates.projectedUse = {
				...state.projectedUse,
				required: isRequired,
				value: isRequired ? state.projectedUse.value : ''
			}
			break
		}

		// ***********************************************************
		// ! STEP 4.1
		/**  @typesOfWorkRenovation  Quel(s) type(s) de travaux souhaitez-vous effectuer? */
		case STEP4_1FIELDS.typesOfWorkRenovation: {
			const isSectionA = isRenovationSectionA(value)
			const isSectionB = isRenovationSectionB(value)

			//* Renovation Interior SECTION A
			// Q12
			updates.workDescriptionRenovationInterior = {
				...state.workDescriptionRenovationInterior,
				required: isSectionA,
				value: isSectionA ? state.workDescriptionRenovationInterior.value : ''
			}

			//* Renovation SECTION B
			// Q16
			updates.workDescriptionRenovationExterior = {
				...state.workDescriptionRenovationExterior,
				required: isSectionB,
				value: isSectionB ? state.workDescriptionRenovationExterior.value : ''
			}

			// step 5
			updates.isTreeCuttingRequired = {
				...state.isTreeCuttingRequired,
				required: isSectionB,
				value: isSectionB ? state.isTreeCuttingRequired.value : ''
			}

			break
		}

		// *******************************************************
		// ! STEP 4.2
		/**  @typesOfWorkOccupation  Quel(s) type(s) de travaux souhaitez-vous effectuer? */
		case STEP4_2FIELDS.typesOfWorkOccupation: {
			// SECTION A
			const occupationSectionA = isOccupationSectionA(value)
			const case1SectionA = isCase1SectionA(value)
			const case4SectionA = isCase4SectionA(value)
			const case3or5or6SectionA = isCase3or5or6SectionA(value)
			const case3SectionA = isCase3SectionA(value)
			const case5SectionA = isCase5SectionA(value)
			const case6SectionA = isCase6SectionA(value)

			// Affichage conditionnel de la question 9
			// Case 1 = afficher la question 9 de la section A
			// Case 3 = afficher la question 9.1 et 9.2 de la section A
			// Case 4 = afficher la question 9.2 de la section A
			// Case 5 = afficher les questions 9.2 et 9.3 de la section A
			// Case 6 = afficher les questions 9.2 et 9.4 de la section A

			{
				/* Question 9 */
			}
			updates.projectedUseNewOccupation = {
				...state.projectedUseNewOccupation,
				required: case1SectionA,
				value: case1SectionA ? state.projectedUseNewOccupation.value : ''
			}

			{
				/* Question 9.1 */
			}
			updates.projectedUseOccupation = {
				...state.projectedUseOccupation,
				required: case3SectionA,
				value: case3SectionA ? state.projectedUseOccupation.value : ''
			}

			{
				/* Question 9.2 */
			}
			updates.currentUseOccupation = {
				...state.currentUseOccupation,
				required: case4SectionA || case3or5or6SectionA,
				value:
					case4SectionA || case3or5or6SectionA
						? state.currentUseOccupation.value
						: ''
			}

			{
				/* Question 9.3 */
			}
			updates.additionalProjectedUseOccupation = {
				...state.additionalProjectedUseOccupation,
				required: case5SectionA,
				value: case5SectionA ? state.additionalProjectedUseOccupation.value : ''
			}

			{
				/* Question 9.4 */
			}
			updates.conditionalProjectedUseOccupation = {
				...state.conditionalProjectedUseOccupation,
				required: case6SectionA,
				value: case6SectionA
					? state.conditionalProjectedUseOccupation.value
					: ''
			}
			// Sous-section : Description de l'occupation
			const occupantDescriptionSectionA = [
				STEP4_2FIELDS.occupantNameSectionA,
				STEP4_2FIELDS.occupiedAreaSectionA,
				STEP4_2FIELDS.occupationActivityDescription
			]

			occupantDescriptionSectionA.forEach((key) => {
				if (state[key]) {
					updates[key] = {
						...state[key],
						required: occupationSectionA,
						value: occupationSectionA ? state[key].value : ''
					}
				}
			})

			// *******************************************************
			// SECTION B
			const occupationSectionB = isOccupationSectionB(value)

			const fieldsSectionB = [
				STEP4_2FIELDS.isLocalOrTerrain,
				STEP4_2FIELDS.occupationActivityType,
				STEP4_2FIELDS.occupantNameSectionB,
				STEP4_2FIELDS.occupiedAreaSectionB
			]

			fieldsSectionB.forEach((key) => {
				if (state[key]) {
					updates[key] = {
						...state[key],
						required: occupationSectionB,
						value: occupationSectionB ? state[key].value : ''
					}
				}
			})

			updates.occupationActivityTypeDetails = {
				...state.occupationActivityTypeDetails,
				required:
					occupationSectionB &&
					state.occupationActivityType.value ==
						REQUEST_CONFIGS_KEYS.occupationActivityType.autres,
				value:
					occupationSectionB &&
					state.occupationActivityType.value ==
						REQUEST_CONFIGS_KEYS.occupationActivityType.autres
						? state.occupationActivityTypeDetails.value
						: ''
			}

			const fields19to21Reset = [
				STEP4_2FIELDS.isAlcoholPermitRequired,
				STEP4_2FIELDS.alcoholPermitActivityType,
				STEP4_2FIELDS.alcoholPermitActivityTypeDetails,
				STEP4_2FIELDS.isMunicipalCertificateRequired,
				STEP4_2FIELDS.isTerraceModificationRequired
			]

			if (occupationSectionB) {
				fields19to21Reset.forEach((key) => {
					if (state[key]) {
						updates[key] = {
							...state[key],
							required: false,
							value: ''
						}
					}
				})
			}

			// *******************************************************
			// SECTION C
			const occupationSectionC = isOccupationSectionC(value)

			updates.conditionalUseAuthorizationRequest = {
				...state.conditionalUseAuthorizationRequest,
				required: occupationSectionC,
				value: occupationSectionC
					? state.conditionalUseAuthorizationRequest.value
					: ''
			}
			updates.conditionalUseNumber = {
				...state.conditionalUseNumber,
				required: false,
				value: ''
			}

			// *******************************************************
			// SECTION D
			const occupationSectionD = isOccupationSectionD(value)

			updates.startDateOccupation = {
				...state.startDateOccupation,
				required: occupationSectionD,
				value: occupationSectionD ? state.startDateOccupation.value : ''
			}
			updates.endDateOccupation = {
				...state.endDateOccupation,
				required: occupationSectionB,
				value: occupationSectionB ? state.endDateOccupation.value : ''
			}

			// *******************************************************
			// SECTION D
			const occupationSectionE = isOccupationSectionE(value)

			updates.isLocataireOccupation = {
				...state.isLocataireOccupation,
				required: occupationSectionE,
				value: occupationSectionE ? state.isLocataireOccupation.value : ''
			}

			break
		}

		case STEP4_2FIELDS.isLocalOrTerrain: {
			updates.occupationActivityType = {
				...state.occupationActivityType,
				value: ''
			}
			break
		}

		case STEP4_2FIELDS.conditionalUseAuthorizationRequest: {
			// SECTION C
			const occupationSectionC = isOccupationSectionC(
				state.typesOfWorkOccupation.value
			)

			updates.conditionalUseNumber = {
				...state.conditionalUseNumber,
				required: occupationSectionC && value == 'true',
				value:
					occupationSectionC && value == 'true'
						? state.conditionalUseNumber.value
						: ''
			}
		}

		case STEP4_2FIELDS.occupationActivityType: {
			updates.occupationActivityTypeDetails = {
				...state.occupationActivityTypeDetails,
				required: value == REQUEST_CONFIGS_KEYS.occupationActivityType.autres,
				value:
					value == REQUEST_CONFIGS_KEYS.occupationActivityType.autres
						? state.occupationActivityTypeDetails.value
						: ''
			}
			break
		}

		case STEP4_2FIELDS.isAlcoholPermitRequired: {
			const isYes =
				value == 'true' &&
				!isOccupationSectionB(state.typesOfWorkOccupation.value)
			updates.alcoholPermitActivityType = {
				...state.alcoholPermitActivityType,
				required: isYes,
				value: isYes ? state.alcoholPermitActivityType.value : ''
			}
			updates.alcoholPermitActivityTypeDetails = {
				...state.alcoholPermitActivityTypeDetails,
				required: false,
				value: ''
			}

			break
		}

		case STEP4_2FIELDS.alcoholPermitActivityType: {
			const isYes =
				value == REQUEST_CONFIGS_KEYS.alcoholPermitActivityType.autre
			updates.alcoholPermitActivityTypeDetails = {
				...state.alcoholPermitActivityTypeDetails,
				required: isYes,
				value: isYes ? state.alcoholPermitActivityTypeDetails.value : ''
			}
			break
		}

		// ********************************************************
		// ! STEP 4.3
		case STEP4_3FIELDS.typesOfWorkHomeOffice: {
			const isOther =
				value == REQUEST_CONFIGS_KEYS.homeOfficeOrBusinessType.others

			updates.typesOfWorkHomeOfficeDetails = {
				...state.typesOfWorkHomeOfficeDetails,
				required: isOther,
				value: isOther ? state.typesOfWorkHomeOfficeDetails.value : ''
			}
			break
		}

		// ********************************************************
		// ! STEP 4.4
		case STEP4_4FIELDS.typesOfWorkEnseigne: {
			const isNewSign = isNewSignInstallation(value)
			const isReplaceSign = isReplaceSignInstallation(value)
			const isArtisticWallRepresentation =
				isArtisticWallRepresentationInstallation(value)

			updates.newEnseignesNumber = {
				...state.newEnseignesNumber,
				required: isNewSign,
				value: isNewSign ? state.newEnseignesNumber.value : ''
			}

			updates.replacedEnseignesNumber = {
				...state.replacedEnseignesNumber,
				required: isReplaceSign,
				value: isReplaceSign ? state.replacedEnseignesNumber.value : ''
			}

			updates.muralRepresentationsNumber = {
				...state.muralRepresentationsNumber,
				required: isArtisticWallRepresentation,
				value: isArtisticWallRepresentation
					? state.muralRepresentationsNumber.value
					: ''
			}
			break
		}

		// *********************************************************
		// ! STEP 5
		/**  @isWorkDueToDisaster  Effectuez-vous ces travaux à la suite d'une catastrophe? */
		case STEP5FIELDS.isWorkDueToDisaster: {
			const isYes = value == 'true'

			updates.workDueToDisasterPrecision = {
				...state.workDueToDisasterPrecision,
				required: isYes,
				value: isYes ? state.workDueToDisasterPrecision.value : ''
			}
			break
		}

		/**  @whoWillBeInChargeOfWork  Qui sera en charge des travaux? */
		/**  @entrepreneurUnknown1  Je ne connais pas encore l'entrepreneur */
		case STEP5FIELDS.whoWillBeInChargeOfWork:
		case STEP5FIELDS.entrepreneurUnknown1: {
			const fieldsToMakeUpdate = [
				STEP5FIELDS.companyNameExecutingWork,
				STEP5FIELDS.companyNEQNumber,
				STEP5FIELDS.companyRBQNumber,
				STEP5FIELDS.companyRepresentativeName,
				STEP5FIELDS.companyRepresentativePhoneNumber,
				STEP5FIELDS.companyRepresentativeEmail,
				STEP5FIELDS.companyAddress,
				STEP5FIELDS.companyCity,
				STEP5FIELDS.companyPostalCode
			]

			let isRequired = false
			if (changedField == STEP5FIELDS.whoWillBeInChargeOfWork) {
				// If the whoWillBeInChargeOfWork field is updated, we reset the value to false
				updates.entrepreneurUnknown1 = {
					...state.entrepreneurUnknown1,
					value: 'false'
				}

				isRequired = !(
					value == REQUEST_CONFIGS_KEYS.whoWillBeInChargeOfWork.firstOption
				)
			} else {
				isRequired = !(value.toString() == 'true')
			}

			fieldsToMakeUpdate.forEach((key) => {
				if (state[key]) {
					updates[key] = {
						...state[key],
						required: isRequired,
						value: isRequired ? state[key].value : ''
					}
				}
			})
			break
		}

		// *********************************************************
		// ! STEP 5
		case STEP6FIELDS.isWaterAndSewerConnectionRequired: {
			// !important RESET FIELDS of Steps 6
			// const isGR7_1 = isGR7_1RenovationOrDevelopment(state.whatToDo.value)

			const fieldsToReset = Object.values(STEP6FIELDS).splice(1) // remove the first element of the array

			if (value == 'false') {
				fieldsToReset.forEach((element) => {
					if (state[element]) {
						switch (element) {
							case STEP6FIELDS.entrepreneurUnknown:
								updates.entrepreneurUnknown = {
									...state.entrepreneurUnknown,
									value: 'false'
								}
								break

							default:
								updates[element] = {
									...state[element],
									required: false,
									value: ''
								}
						}
					}
				})
			}
			break
		}

		case STEP6FIELDS.plannedWork: {
			const isAdditionalInfoQ44Required = value.includes(
				REQUEST_CONFIGS_KEYS.plannedWork.seventhOption
			)
			updates.additionalInfoQ44 = {
				...state.additionalInfoQ44,
				required: isAdditionalInfoQ44Required,
				value: isAdditionalInfoQ44Required ? state.additionalInfoQ44.value : ''
			}

			const isEntrepreneurFieldsRequired =
				pageUtils.isEntrepreneurFieldsRequired(value) &&
				state?.entrepreneurUnknown.value?.toString() == 'false'

			const entrepreneurFields = [
				STEP6FIELDS.entrepreneurName,
				STEP6FIELDS.entrepreneurRBQNumber,
				STEP6FIELDS.entrepreneurRepresentativeName,
				STEP6FIELDS.entrepreneurRepresentativePhoneNumber,
				STEP6FIELDS.entrepreneurRepresentativeEmail,
				STEP6FIELDS.entrepreneurRepresentativeAddress,
				STEP6FIELDS.entrepreneurRepresentativeCity,
				STEP6FIELDS.entrepreneurRepresentativePostalCode
			]

			entrepreneurFields.forEach((key) => {
				if (state[key]) {
					updates[key] = {
						...state[key],
						required: isEntrepreneurFieldsRequired,
						value: isEntrepreneurFieldsRequired ? state[key].value : ''
					}
				}
			})
			break
		}

		case STEP6FIELDS.entrepreneurUnknown: {
			const isEntrepreneurFieldsRequired =
				pageUtils.isEntrepreneurFieldsRequired(state.plannedWork.value) &&
				value?.toString() == 'false'

			const entrepreneurFields = [
				STEP6FIELDS.entrepreneurName,
				STEP6FIELDS.entrepreneurRBQNumber,
				STEP6FIELDS.entrepreneurRepresentativeName,
				STEP6FIELDS.entrepreneurRepresentativePhoneNumber,
				STEP6FIELDS.entrepreneurRepresentativeEmail,
				STEP6FIELDS.entrepreneurRepresentativeAddress,
				STEP6FIELDS.entrepreneurRepresentativeCity,
				STEP6FIELDS.entrepreneurRepresentativePostalCode
			]

			entrepreneurFields.forEach((key) => {
				if (state[key]) {
					updates[key] = {
						...state[key],
						required: isEntrepreneurFieldsRequired,
						value: isEntrepreneurFieldsRequired ? state[key].value : ''
					}
				}
			})
			break
		}

		default:
			break
	}

	return updates
}

// --- 3. Reducer centralisé ---
export const inputReducer = (state, action) => {
	switch (action.type) {
		case 'UPDATE_INPUT': {
			const { name, value } = action
			let newState = {
				...state,
				[name]: {
					...state[name],
					value
				}
			}
			const linked = updateLinkedFields(newState, name, value)
			return {
				...newState,
				...linked
			}
		}
		case 'RESET':
			return action.payload
		default:
			return state
	}
}
